import { useState } from "react";
import { toast } from "sonner";
import { Volume2, Turtle, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

export default function LessonView({ lesson }) {
    return (
        <div className="space-y-8 animate-fade-up" data-testid="lesson-view">
            {/* Objective */}
            <section
                className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-8"
                data-testid="lesson-objective-section"
            >
                <p className="text-xs tracking-[0.24em] uppercase text-inkSoft mb-3">
                    Objectif · {lesson.target_language} · {lesson.level}
                </p>
                <h2
                    className="font-serif text-2xl sm:text-3xl leading-snug tracking-tight"
                    data-testid="lesson-objective-text"
                >
                    {lesson.objective}
                </h2>
                <p className="mt-3 text-sm text-inkSoft">
                    Thème : <span className="italic">{lesson.theme}</span>
                </p>
            </section>

            {/* Vocabulary */}
            <section className="space-y-4" data-testid="vocabulary-section">
                <div className="flex items-baseline justify-between">
                    <h3 className="font-serif text-2xl">Vocabulaire</h3>
                    <span className="text-xs tracking-[0.2em] uppercase text-inkSoft">
                        {lesson.vocabulary.length} mots
                    </span>
                </div>

                <div className="space-y-4">
                    {lesson.vocabulary.map((item, idx) => (
                        <VocabularyCard
                            key={idx}
                            index={idx}
                            item={item}
                            language={lesson.target_language}
                        />
                    ))}
                </div>
            </section>

            {/* Mnemonic */}
            {lesson.mnemonic_scene && (
                <section
                    className="bg-emerald text-white rounded-2xl p-6 md:p-8"
                    data-testid="mnemonic-section"
                >
                    <p className="text-[11px] tracking-[0.28em] uppercase text-white/70 mb-3">
                        Scène mnémotechnique
                    </p>
                    <p
                        className="font-serif text-lg sm:text-xl leading-relaxed italic"
                        data-testid="mnemonic-text"
                    >
                        « {lesson.mnemonic_scene} »
                    </p>
                </section>
            )}
        </div>
    );
}

function VocabularyCard({ item, index, language }) {
    const [loading, setLoading] = useState({ word: null, sentence: null });

    const playAudio = async (kind, text, speed) => {
        const stateKey = kind;
        setLoading((s) => ({ ...s, [stateKey]: speed }));
        try {
            const { data } = await api.post("/poc/tts", { text, language, speed });
            const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
            await audio.play();
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || "Erreur audio.";
            toast.error(`TTS : ${detail}`);
        } finally {
            setLoading((s) => ({ ...s, [stateKey]: null }));
        }
    };

    const registerColor =
        item.register === "familier"
            ? "bg-terracotta/10 text-terracotta border-terracotta/30"
            : item.register === "formel"
              ? "bg-emerald/10 text-emerald border-emerald/30"
              : "bg-paperAlt text-inkSoft border-lexiBorder";

    return (
        <article
            className="bg-white border border-lexiBorder rounded-2xl p-6 md:p-8 hover:shadow-md hover:shadow-black/5 hover:-translate-y-0.5 transition-transform duration-300 ease-out"
            data-testid={`vocab-card-${index}`}
        >
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-lexiBorder">
                <div className="min-w-0">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                        {item.grammatical_category}
                    </p>
                    <h4
                        className="font-serif text-3xl md:text-4xl leading-none tracking-tight break-words"
                        data-testid={`vocab-word-${index}`}
                    >
                        {item.word}
                    </h4>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                        <span
                            className="font-mono text-emerald"
                            data-testid={`vocab-ipa-${index}`}
                        >
                            {item.ipa_pronunciation}
                        </span>
                        <span className="text-inkSoft">·</span>
                        <span
                            className="text-inkSoft italic"
                            data-testid={`vocab-simplified-${index}`}
                        >
                            {item.simplified_pronunciation_for_french_speakers}
                        </span>
                        <span
                            className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full border ${registerColor}`}
                            data-testid={`vocab-register-${index}`}
                        >
                            {item.register}
                        </span>
                    </div>
                </div>

                {/* Word audio buttons */}
                <div className="flex flex-wrap gap-2">
                    <AudioButton
                        onClick={() => playAudio("word", item.word, "natural")}
                        loading={loading.word === "natural"}
                        icon={Volume2}
                        label="Écouter"
                        variant="primary"
                        testId={`vocab-word-audio-natural-${index}`}
                    />
                    <AudioButton
                        onClick={() => playAudio("word", item.word, "slow")}
                        loading={loading.word === "slow"}
                        icon={Turtle}
                        label="Lentement"
                        variant="secondary"
                        testId={`vocab-word-audio-slow-${index}`}
                    />
                </div>
            </div>

            {/* Body */}
            <div className="mt-4 space-y-3">
                <p
                    className="text-base text-ink"
                    data-testid={`vocab-translation-${index}`}
                >
                    <span className="text-inkSoft text-xs tracking-[0.2em] uppercase mr-2">
                        FR
                    </span>
                    {item.translation_fr}
                </p>

                <div className="bg-paper border border-lexiBorder rounded-xl p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p
                            className="font-serif text-lg italic leading-snug"
                            data-testid={`vocab-sentence-${index}`}
                        >
                            « {item.natural_sentence} »
                        </p>
                        <p
                            className="text-sm text-inkSoft mt-1"
                            data-testid={`vocab-sentence-fr-${index}`}
                        >
                            {item.sentence_translation_fr}
                        </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <AudioButton
                            onClick={() =>
                                playAudio("sentence", item.natural_sentence, "natural")
                            }
                            loading={loading.sentence === "natural"}
                            icon={Volume2}
                            label="Écouter"
                            variant="primary"
                            small
                            testId={`vocab-sentence-audio-natural-${index}`}
                        />
                        <AudioButton
                            onClick={() =>
                                playAudio("sentence", item.natural_sentence, "slow")
                            }
                            loading={loading.sentence === "slow"}
                            icon={Turtle}
                            label="Lentement"
                            variant="secondary"
                            small
                            testId={`vocab-sentence-audio-slow-${index}`}
                        />
                    </div>
                </div>

                {item.trap_or_false_friend && (
                    <div
                        className="flex items-start gap-2 rounded-xl p-3 bg-terracotta/8 border border-terracotta/25 text-sm text-ink"
                        data-testid={`vocab-trap-${index}`}
                    >
                        <AlertTriangle
                            className="w-4 h-4 mt-0.5 text-terracotta shrink-0"
                            strokeWidth={1.75}
                        />
                        <span>
                            <span className="text-terracotta font-semibold">Attention :</span>{" "}
                            {item.trap_or_false_friend}
                        </span>
                    </div>
                )}
            </div>
        </article>
    );
}

function AudioButton({ onClick, loading, icon: Icon, label, variant, small, testId }) {
    const base =
        "inline-flex items-center gap-1.5 rounded-full font-medium transition-colors active:scale-[0.97] disabled:opacity-60";
    const size = small ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";
    const styles =
        variant === "primary"
            ? "bg-ink text-white hover:bg-emerald"
            : "bg-paperAlt text-ink border border-lexiBorder hover:bg-white";
    return (
        <button
            onClick={onClick}
            disabled={loading}
            data-testid={testId}
            className={`${base} ${size} ${styles}`}
        >
            {loading ? (
                <Loader2 className={small ? "w-3.5 h-3.5 animate-spin" : "w-4 h-4 animate-spin"} />
            ) : (
                <Icon className={small ? "w-3.5 h-3.5" : "w-4 h-4"} strokeWidth={1.75} />
            )}
            <span>{label}</span>
        </button>
    );
}
